import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  ArrowLeft, CheckCircle2, XCircle, AlertTriangle, Loader2,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Pill, Button, Field, Input, Textarea, Spinner, EmptyState,
} from "@/components/ui";
import { fmtDateTime } from "@/lib/dates";

// =============================================================================
// Client acceptance / rejection screen
// =============================================================================
// Route: /packs/:packId/sign-off
//
// Available to managers+ for packs in status 'published' or 'accepted'.
// Records the client's decision (accept / reject) with contact details.
//
// Phase 4 will add a public hash-token variant (hse.coretelgroup.com/accept/:token)
// so clients can sign off directly without needing a Coretel login. For now,
// we record the sign-off internally on the client's behalf.
// =============================================================================

export default function ClientAcceptanceScreen() {
  const { packId } = useParams<{ packId: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();

  const [decision, setDecision] = useState<"accept" | "reject" | null>(null);
  const [client_contact_name,  setContactName]  = useState("");
  const [client_contact_email, setContactEmail] = useState("");
  const [client_po_reference,  setPoRef]        = useState("");
  const [rejection_notes,      setRejectionNotes] = useState("");

  const { data: pack, isLoading, refetch } = useQuery({
    queryKey: ["pack_signoff", packId],
    enabled: !!packId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("acceptance_packs")
        .select(`
          pack_id, pack_number, pack_title, status,
          published_at, sharepoint_url,
          client_accepted_at, client_accepted_by_contact,
          client_rejected_at, client_rejection_notes,
          projects(project_code, project_name, client_id, clients(legal_name))
        `)
        .eq("pack_id", packId!)
        .maybeSingle();
      if (error) throw error;
      return data as any;
    },
  });

  const acceptMut = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("acceptance_packs").update({
        status: "accepted",
        client_accepted_at: new Date().toISOString(),
        client_accepted_by_contact: {
          name:  client_contact_name,
          email: client_contact_email || null,
          po:    client_po_reference || null,
          recorded_by_user_id: user?.user_id,
          recorded_at: new Date().toISOString(),
        },
      }).eq("pack_id", packId!);
      if (error) throw error;

      // Raise an event so downstream systems (invoicing) know the job is done
      await supabase.from("notifications").insert({
        channel: "in_app",
        event_type: "acceptance_pack.client_accepted",
        payload: { pack_id: packId },
        related_table: "acceptance_packs",
        related_id: packId,
      });
    },
    onSuccess: () => { refetch(); setDecision(null); },
  });

  const rejectMut = useMutation({
    mutationFn: async () => {
      if (!rejection_notes.trim()) throw new Error("reason_required");
      const { error } = await supabase.from("acceptance_packs").update({
        status: "rejected_by_client",
        client_rejected_at: new Date().toISOString(),
        client_rejection_notes: rejection_notes.trim(),
        client_accepted_by_contact: {
          name:  client_contact_name,
          email: client_contact_email || null,
          recorded_by_user_id: user?.user_id,
          recorded_at: new Date().toISOString(),
        },
      }).eq("pack_id", packId!);
      if (error) throw error;

      // Directors get notified so they can plan re-work
      await supabase.from("notifications").insert({
        channel: "in_app",
        event_type: "acceptance_pack.client_rejected",
        payload: { pack_id: packId, reason: rejection_notes.trim() },
        related_table: "acceptance_packs",
        related_id: packId,
        priority: 2,
      });
    },
    onSuccess: () => { refetch(); setDecision(null); },
  });

  if (isLoading) return <div className="py-20 flex justify-center"><Spinner /></div>;
  if (!pack) return <div className="p-6">Pack not found.</div>;

  const canSignOff = hasRole(user, "manager") && (pack.status === "published" || pack.status === "ready_to_publish");
  const already = pack.status === "accepted" || pack.status === "rejected_by_client";

  return (
    <div className="max-w-xl mx-auto p-4 md:p-6">
      <button onClick={() => navigate(`/packs/${packId}`)} className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft size={14}/> Pack detail
      </button>

      <div className="mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[13px] text-accent">{pack.pack_number}</span>
          <Pill
            label={pack.status.replace(/_/g, " ")}
            tone={pack.status === "accepted" ? "approved" : pack.status === "rejected_by_client" ? "critical" : "mobilising"}
          />
        </div>
        <h1 className="text-[22px] font-semibold tracking-tight mt-1">Client sign-off</h1>
        <div className="text-text-muted text-[13px] mt-1">
          {pack.pack_title}
          <br />
          {pack.projects?.project_code} · {pack.projects?.clients?.legal_name ?? "—"}
        </div>
      </div>

      {/* Already decided */}
      {already && (
        <Card>
          <div className="p-5">
            {pack.status === "accepted" ? (
              <div className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-ok mt-0.5" />
                <div>
                  <div className="text-[15px] font-semibold text-ok">Client accepted</div>
                  <div className="text-[12px] text-text-muted mt-1 font-mono">
                    {fmtDateTime(pack.client_accepted_at)}
                    {pack.client_accepted_by_contact?.name && ` · ${pack.client_accepted_by_contact.name}`}
                    {pack.client_accepted_by_contact?.po && ` · PO ${pack.client_accepted_by_contact.po}`}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <XCircle size={20} className="text-danger mt-0.5" />
                <div>
                  <div className="text-[15px] font-semibold text-danger">Client rejected</div>
                  <div className="text-[12px] text-text-muted mt-1 font-mono">
                    {fmtDateTime(pack.client_rejected_at)}
                  </div>
                  {pack.client_rejection_notes && (
                    <div className="text-[13px] text-text mt-3 bg-danger/10 border border-danger/20 rounded p-3">
                      {pack.client_rejection_notes}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Sign-off form */}
      {!already && canSignOff && (
        <Card>
          <div className="p-5">
            <div className="text-[13px] text-text-muted mb-4">
              Record the client's decision on behalf of {pack.projects?.clients?.legal_name ?? "the client"}.
              This is recorded as attestation against your user account.
            </div>

            {decision === null && (
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setDecision("accept")}
                  className="py-5 px-3 rounded border-2 border-ok/30 bg-ok/5 text-ok hover:bg-ok/15 transition-colors"
                >
                  <CheckCircle2 size={22} className="mx-auto mb-2" />
                  <div className="font-semibold text-[14px]">Accept</div>
                  <div className="text-[11px] text-text-muted mt-1">Client happy with the work</div>
                </button>
                <button
                  onClick={() => setDecision("reject")}
                  className="py-5 px-3 rounded border-2 border-danger/30 bg-danger/5 text-danger hover:bg-danger/15 transition-colors"
                >
                  <XCircle size={22} className="mx-auto mb-2" />
                  <div className="font-semibold text-[14px]">Reject</div>
                  <div className="text-[11px] text-text-muted mt-1">Re-work needed</div>
                </button>
              </div>
            )}

            {decision === "accept" && (
              <div className="space-y-3">
                <Field label="Client contact name" hint="Whoever signed off at the client's end.">
                  <Input value={client_contact_name} onChange={e => setContactName(e.target.value)} autoFocus placeholder="e.g. Jane Smith" />
                </Field>
                <Field label="Email (optional)">
                  <Input type="email" value={client_contact_email} onChange={e => setContactEmail(e.target.value)} placeholder="jane@example.com" />
                </Field>
                <Field label="PO / reference (optional)">
                  <Input mono value={client_po_reference} onChange={e => setPoRef(e.target.value)} placeholder="e.g. PO-45678" />
                </Field>
                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="secondary" onClick={() => setDecision(null)}>Back</Button>
                  <Button icon={CheckCircle2} onClick={() => acceptMut.mutate()} disabled={!client_contact_name.trim() || acceptMut.isPending}>
                    {acceptMut.isPending ? "Saving…" : "Record acceptance"}
                  </Button>
                </div>
              </div>
            )}

            {decision === "reject" && (
              <div className="space-y-3">
                <Field label="Client contact name">
                  <Input value={client_contact_name} onChange={e => setContactName(e.target.value)} autoFocus placeholder="e.g. Jane Smith" />
                </Field>
                <Field label="Email (optional)">
                  <Input type="email" value={client_contact_email} onChange={e => setContactEmail(e.target.value)} />
                </Field>
                <Field label="What needs fixing?" hint="This becomes the brief for re-work — be specific.">
                  <Textarea rows={4} value={rejection_notes} onChange={e => setRejectionNotes(e.target.value)} placeholder="e.g. Cable tray on roof needs rework — droops below spec on northern run." />
                </Field>
                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="secondary" onClick={() => setDecision(null)}>Back</Button>
                  <Button variant="danger" icon={XCircle} onClick={() => rejectMut.mutate()} disabled={!rejection_notes.trim() || !client_contact_name.trim() || rejectMut.isPending}>
                    {rejectMut.isPending ? "Saving…" : "Record rejection"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {!already && !canSignOff && (
        <Card>
          <EmptyState
            title="Not ready for sign-off"
            description={
              pack.status === "draft" || pack.status === "in_progress" || pack.status === "qa_review"
                ? "The pack isn't published yet."
                : "You don't have permission to record client sign-off."
            }
            icon={AlertTriangle}
          />
        </Card>
      )}
    </div>
  );
}
